// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Route guards for the patient onboarding flow. Without these, direct navigation or a
// mid-flow reload lands on a screen with no session — letting a visitor reach consent/chat
// with no identity, or hit the phone-enrollment endpoints with no bound session. Guards
// resolve each protected route against the session lifecycle and redirect to an honest
// terminal ("get a new link") rather than a dead-ended re-verify of a consumed token.

import { Navigate } from 'react-router-dom'
import { getSessionState, getDeeplinkToken, clearSession } from '@/lib/session'
import { isFirebaseReady } from '@/lib/firebase'
import { AuthUnavailable } from '@/components/AuthUnavailable'

// Gates the post-verify steps (/phone, /link-google, /consent, /chat) on a live session.
export function RequireSession({ children }: { children: React.ReactElement }) {
  const state = getSessionState()
  if (state === 'valid') return children
  if (state === 'expired') {
    clearSession()
    return <Navigate to="/link-expired" replace />
  }
  // Absent: a still-unconsumed deeplink token means verify simply hasn't run yet.
  if (getDeeplinkToken()) return <Navigate to="/verify" replace />
  return <Navigate to="/link-expired" replace />
}

// Gates the Firebase-dependent steps (/phone, /link-google): if the app is misconfigured
// (missing env), degrade to a clear "unavailable" screen instead of a white-screen SDK crash.
export function RequireFirebase({ children }: { children: React.ReactElement }) {
  if (!isFirebaseReady) return <AuthUnavailable />
  return children
}

// Gates /verify. A live session means the patient already verified — send them forward to the
// next onboarding step (/link-google) instead of letting them re-submit a consumed single-use
// token (which dead-ends at /link-invalidated). No token at all is a stray visit → terminal.
export function RequireVerifyEntry({ children }: { children: React.ReactElement }) {
  if (getSessionState() === 'valid') return <Navigate to="/link-google" replace />
  if (getDeeplinkToken()) return children
  return <Navigate to="/link-expired" replace />
}
